// src/components/ui/toast.tsx
import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, XCircle, X, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose?: () => void;
  isVisible?: boolean;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  onClose,
  isVisible = true
}) => {
  const [show, setShow] = useState(isVisible);

  useEffect(() => {
    setShow(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [show, duration]);

  const handleClose = () => {
    setShow(false);
    if (onClose) {
      setTimeout(() => onClose(), 300);
    }
  };

  const getToastConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          bgColor: '#f0fdf4',
          borderColor: '#bbf7d0',
          iconColor: '#22c55e',
          textColor: '#166534',
          progressColor: '#22c55e'
        };
      case 'error':
        return {
          icon: XCircle,
          bgColor: '#fef2f2',
          borderColor: '#fecaca',
          iconColor: '#ef4444',
          textColor: '#991b1b',
          progressColor: '#ef4444'
        };
      case 'warning':
        return {
          icon: AlertCircle,
          bgColor: '#fefce8',
          borderColor: '#fde047',
          iconColor: '#eab308',
          textColor: '#854d0e',
          progressColor: '#eab308'
        };
      default:
        return {
          icon: Info,
          bgColor: '#eff6ff',
          borderColor: '#bfdbfe',
          iconColor: '#3b82f6',
          textColor: '#1e40af',
          progressColor: '#3b82f6'
        };
    }
  };

  const config = getToastConfig();
  const Icon = config.icon;

  if (!show) return null;

  return (
    <div
      style={{
        backgroundColor: config.bgColor,
        borderColor: config.borderColor,
        color: config.textColor,
        border: '1px solid',
        borderRadius: '0.5rem',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        padding: '1rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        minWidth: '320px',
        maxWidth: '28rem',
        transition: 'all 0.3s ease-in-out',
        animation: show ? 'slide-in 0.3s ease-out forwards' : 'slide-out 0.3s ease-in forwards',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Icon style={{ width: '1.25rem', height: '1.25rem', color: config.iconColor, flexShrink: 0, marginTop: '0.125rem' }} />
      
      <div style={{ flex: 1, paddingRight: '0.5rem' }}>
        <p style={{ fontSize: '0.875rem', fontWeight: 500, margin: 0 }}>{message}</p>
      </div>

      <button
        onClick={handleClose}
        style={{
          color: config.iconColor,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          transition: 'opacity 0.2s',
          flexShrink: 0
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        aria-label="Close notification"
      >
        <X style={{ width: '1rem', height: '1rem' }} />
      </button>

      {duration > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '0.25rem',
          backgroundColor: '#e5e7eb',
          borderRadius: '0 0 0.5rem 0.5rem',
          overflow: 'hidden'
        }}>
          <div
            style={{
              height: '100%',
              backgroundColor: config.progressColor,
              transition: 'all ease-linear',
              animation: `progress ${duration}ms linear forwards`
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slide-out {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        @keyframes progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: Array<ToastProps & { id: string }>;
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  return (
    <div style={{
      position: 'fixed',
      top: '1rem',
      right: '1rem',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      pointerEvents: 'none',
      gap: '0.5rem'
    }}>
      <div style={{ pointerEvents: 'auto' }}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={() => onRemove(toast.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Hook to manage toasts
export const useToast = () => {
  const [toasts, setToasts] = useState<Array<ToastProps & { id: string }>>([]);

  const showToast = (message: string, type: ToastType = 'info', duration: number = 5000) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration + 300);
    }
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const clearAll = () => {
    setToasts([]);
  };

  return {
    toasts,
    showToast,
    removeToast,
    clearAll,
    success: (message: string, duration?: number) => showToast(message, 'success', duration),
    error: (message: string, duration?: number) => showToast(message, 'error', duration),
    warning: (message: string, duration?: number) => showToast(message, 'warning', duration),
    info: (message: string, duration?: number) => showToast(message, 'info', duration),
  };
};

export default Toast;